import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useDispatch } from '../../Redux/CustomHooks';
import { RootState } from '../../Redux/Store';
import { fetchCompanyData } from '../../Redux/Company/companysSlice';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Container,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import { getStoredMemberships, persistSession, switchStore } from '../../Redux/Auth/authApi';

// サブスク状態の表示ラベル。
const SUBSCRIPTION_LABEL: Record<string, string> = {
  INACTIVE: '未課金',
  TRIAL: '無料トライアル中',
  ACTIVE: '課金中',
  GRACE: '支払い猶予中',
  EXPIRED: '期限切れ',
  EXEMPT: '課金免除（特権）',
};
const subscriptionLabel = (status?: string): string => (status ? SUBSCRIPTION_LABEL[status] ?? status : '不明');

const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const companyData = useSelector((state: RootState) => state.company);
  const [error, setError] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);

  // 複数店舗に所属している場合、ヘッダーで店舗を切り替えられるようにする。
  const memberships = getStoredMemberships();
  const currentStoreId = localStorage.getItem('store_id') ?? '';

  const handleSwitchStore = (e: SelectChangeEvent) => {
    const storeId = e.target.value;
    if (!storeId || storeId === currentStoreId || switching) return;
    setSwitching(true);
    switchStore(storeId)
      .then((auth) => {
        persistSession(auth);
        // 店舗切替時は Redux/キャッシュに前店舗のデータ（テーブル・メニュー等）が残るため、
        // フルリロードで全状態をリフレッシュして取り違えを防ぐ。
        window.location.href = `/home?storeClerkId=${auth.clerkId}`;
      })
      .catch((err: Error) => {
        console.error('Error:', err);
        setError(
          err.message === 'STORE_LOCKED'
            ? 'この店舗は削除リクエスト中のため利用できません。'
            : '店舗の切り替えに失敗しました。再度ログインしてお試しください。',
        );
      })
      .finally(() => setSwitching(false));
  };

  useEffect(() => {
    if (!companyData.name) {
      dispatch(fetchCompanyData())
        .unwrap()
        .catch(() => setError('Failed to fetch company data.'));
    }
  }, [dispatch, companyData.name]);

  if (error) {
    return (
      <Alert severity="error" sx={{ textAlign: 'center', m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!companyData.name) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppBar 
      position="static" 
      color="default" 
      elevation={1}
      sx={{
        px: 1,
        backgroundColor: '#f9f9f9',
        minWidth: '390px',
        width: '100%',
      }}
    >
      <Container maxWidth={false}>
        <Toolbar disableGutters>
          <IconButton
            color="primary"
            onClick={() => navigate(`/home?storeClerkId=${companyData.stores[0].clerks[0].id}`)}
            sx={{
              backgroundColor: '#F46B4D',
              color: '#fff',
              mr: 2,
              '&:hover': {
                backgroundColor: '#E15D40',
              },
            }}
          >
            <HomeIcon />
          </IconButton>

          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center',
            flex: '1 1 auto',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}>
            {memberships.length > 1 ? (
              // 複数店舗所属時は店舗切替ドロップダウンを表示（店舗名：サブスク状態）。
              <FormControl size="small" sx={{ minWidth: 200, backgroundColor: '#fff', borderRadius: 1 }}>
                <Select
                  value={currentStoreId}
                  disabled={switching}
                  onChange={handleSwitchStore}
                  displayEmpty
                >
                  {memberships.map((m) => {
                    // サブスク状態は MANAGER のみに開示。CLERK にはロック以外の追加情報を出さない。
                    const isManager = localStorage.getItem('role') === 'ROLE_MANAGER';
                    const suffix = m.locked
                      ? '：削除リクエスト中'
                      : isManager
                        ? `：${subscriptionLabel(m.subscriptionStatus)}`
                        : '';
                    return (
                      <MenuItem key={m.storeId} value={m.storeId} disabled={!!m.locked}>
                        {m.storeName}{suffix}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            ) : (
              companyData.stores.map((store) => (
                <Box key={store.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ whiteSpace: 'nowrap' }}>
                    {store.name}
                  </Typography>
                  {store.clerks.map((clerk) => (
                    <Chip
                      key={clerk.name}
                      label={clerk.name}
                      size="small"
                      sx={{
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                      }}
                    />
                  ))}
                </Box>
              ))
            )}
          </Box>

          <Typography
            variant="h6"
            component="h1"
            sx={{
              ml: 2,
              color: '#333',
              whiteSpace: 'nowrap',
              // モバイルでは店舗名セレクトが潰れるため企業名は表示しない（店舗管理のフッターに表示）。
              display: { xs: 'none', md: 'block' },
            }}
          >
            {companyData.name}
          </Typography>

          <IconButton
            aria-label="ログアウト"
            title="ログアウト"
            onClick={() => navigate('/logout')}
            sx={{ ml: 2, color: '#555' }}
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;